import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getColorsForMotivos } from './motivoColors';
import { getPeriodLabel } from './periodUtils';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Componente base para gráficos de barras horizontales
 * Reutilizable para diferentes tipos de datos (motivos, carreras, duraciones, etc.)
 */
const HorizontalBarChart = ({ 
  data, 
  period, 
  title = "Gráfico",
  topLabel = "Top",
  allLabel = "Todos",
  tooltipSuffix = "",
  defaultTopCount = 5,
  noDataIcon = "bi-graph-up",
  noDataMessage = "No hay datos disponibles para el período seleccionado",
  useConsistentColors = true,
  minHeight = 300,
  barHeight = 70
}) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className={`${noDataIcon} fs-1`}></i>
        <p className="mt-2">{noDataMessage}</p>
      </div>
    );
  }

  // Determinar qué datos mostrar (top N o todos)
  const numItems = mostrarTodos ? data.labels.length : Math.min(defaultTopCount, data.labels.length);
  const labels = data.labels.slice(0, numItems);
  const valores = data.data.slice(0, numItems);

  // Obtener colores (consistentes si useConsistentColors=true, sino colores por defecto)
  let backgroundColors, borderColors;
  
  if (useConsistentColors) {
    const colors = getColorsForMotivos(labels);
    backgroundColors = colors.backgroundColors;
    borderColors = colors.borderColors;
  } else {
    // Color único para todos (usado en CarrerasChart)
    backgroundColors = Array(valores.length).fill('rgba(54, 162, 235, 0.8)');
    borderColors = Array(valores.length).fill('rgba(54, 162, 235, 1)');
  }

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: data.datasetLabel || 'Valor',
        data: valores,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    indexAxis: 'y', // Gráfico horizontal
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.parsed.x}${tooltipSuffix}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          callback: function(value) {
            return data.xAxisFormatter ? data.xAxisFormatter(value) : value;
          }
        },
        grid: {
          display: true,
        }
      },
      y: {
        grid: {
          display: false,
        }
      }
    },
  };

  // Calcular altura dinámica basada en el número de items
  const chartHeight = Math.max(minHeight, numItems * barHeight);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">{title}</h5>
          <small className="text-muted">
            {mostrarTodos ? allLabel : `${topLabel} ${defaultTopCount}`} -{" "}
            {getPeriodLabel(period)?.toLowerCase() || "período seleccionado"}
          </small>
        </div>
        {data.labels.length > defaultTopCount && (
          <button
            className="btn btn-sm btn-outline-primary-blue"
            onClick={() => setMostrarTodos(!mostrarTodos)}
          >
            <i
              className={`bi bi-${mostrarTodos ? "dash" : "plus"}-circle me-1`}
            ></i>
            {mostrarTodos ? "Mostrar menos" : "Ver todos"}
          </button>
        )}
      </div>
      <div style={{ height: `${chartHeight}px` }}>
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-2 text-muted small text-center">
        Total: {data.labels.length}{" "}
        {data.labels.length === 1 ? "registro" : "registros"}
      </div>
    </div>
  );
};

HorizontalBarChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.arrayOf(PropTypes.number).isRequired,
    datasetLabel: PropTypes.string,
    xAxisFormatter: PropTypes.func,
  }),
  period: PropTypes.string.isRequired,
  title: PropTypes.string,
  topLabel: PropTypes.string,
  allLabel: PropTypes.string,
  tooltipSuffix: PropTypes.string,
  defaultTopCount: PropTypes.number,
  noDataIcon: PropTypes.string,
  noDataMessage: PropTypes.string,
  useConsistentColors: PropTypes.bool,
  minHeight: PropTypes.number,
  barHeight: PropTypes.number,
};

export default HorizontalBarChart;
